/**
 * 업로드 전 클라이언트 축소.
 *
 * 최근 스마트폰 사진은 4000px / 10MB를 넘는 일이 흔해서, 서버의 업로드 상한(5MB)에 걸려
 * "파일이 너무 큽니다"만 보게 된다. 서버는 어차피 원본을 1024px로 리사이즈해 저장하므로
 * 브라우저에서 미리 큰 변을 줄여 보내면 사용자는 상한을 의식하지 않아도 된다.
 *
 * 축소하지 않고 원본을 그대로 올리는 경우:
 * - 이미 충분히 작은 이미지 (상한 이하 + 긴 변이 maxEdge 이하)
 * - 문서보안(DRM) 테넌트의 암호화 파일 — 브라우저가 디코딩할 수 없다 (서버가 복호화한다)
 * - 디코딩·인코딩이 실패한 경우 (서버 검증에 맡긴다)
 */

export interface DownscaleOptions {
  /** 긴 변 최대 픽셀 */
  maxEdge: number
  /** 이 크기 이하이고 maxEdge 이하이면 그대로 둔다 */
  maxBytes: number
  /** 축소 후 짧은 변이 이 값보다 작아지면 원본을 유지한다 (서버 최소 크기 검증 회피) */
  minShortEdge?: number
}

const JPEG_QUALITIES = [0.92, 0.85, 0.75]

function replaceExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^./\\]+$/, '')
  return `${base || 'image'}.${ext}`
}

async function decode(file: File): Promise<{ width: number; height: number; draw: CanvasImageSource } | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      return { width: bitmap.width, height: bitmap.height, draw: bitmap }
    } catch {
      // 암호화 파일 등 디코딩 불가 → 원본 유지
    }
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight, draw: img })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
}

/**
 * 필요하면 축소한 새 File 을, 아니면 받은 File 을 그대로 돌려준다. 절대 throw 하지 않는다.
 */
export async function downscaleImageIfNeeded(
  file: File,
  { maxEdge, maxBytes, minShortEdge = 0 }: DownscaleOptions
): Promise<File> {
  if (typeof document === 'undefined') return file

  const decoded = await decode(file)
  if (!decoded || decoded.width === 0 || decoded.height === 0) return file

  const longEdge = Math.max(decoded.width, decoded.height)
  if (file.size <= maxBytes && longEdge <= maxEdge) return file

  const scale = Math.min(1, maxEdge / longEdge)
  const width = Math.max(1, Math.round(decoded.width * scale))
  const height = Math.max(1, Math.round(decoded.height * scale))
  if (Math.min(width, height) < minShortEdge) return file

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  // JPEG는 알파를 지원하지 않는다 → 투명 배경은 흰색으로 평탄화
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)
  ctx.drawImage(decoded.draw, 0, 0, width, height)

  for (const quality of JPEG_QUALITIES) {
    const blob = await toBlob(canvas, quality)
    if (!blob) break
    if (blob.size <= maxBytes || quality === JPEG_QUALITIES[JPEG_QUALITIES.length - 1]) {
      // 축소본이 원본보다 크면(이미 잘 압축된 작은 이미지) 원본을 쓴다
      if (blob.size >= file.size && longEdge <= maxEdge) return file
      return new File([blob], replaceExtension(file.name, 'jpg'), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      })
    }
  }

  return file
}
